<template>
  <div :class="[$style.config, $style.wrap]">
    <div :class="$style.container">
      <header :class="$style.header">
        <BaseHeading as="h3" :class="$style.heading">
          {{ data?.fields?.header }}
        </BaseHeading>

        <MarkedParser :class="$style.copy" :md="data?.fields?.copy" />

        <div v-if="data?.fields?.ctas?.length" :class="$style.ctas">
          <CallToAction
            v-for="c in data?.fields?.ctas"
            :key="c?.sys?.id"
            :cta="c"
          />
        </div>
      </header>
      <div v-if="computedEvents?.length" :class="$style.cards">
        <EventCard v-for="a in computedEvents" :key="a?.sys?.id" :data="a" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  TypeContentBlockWithoutUnresolvableLinksResponse,
  TypeEventSkeleton,
} from '~/types/contentful'

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const { $client } = useNuxtApp()
const { locale } = useI18n()

const computedTags = computed(() =>
  props.data?.fields?.references
    ?.filter?.(instanceOfTag)
    ?.map?.((tag) => tag?.sys?.id)
    ?.join?.(','),
)

const cookieStore = useCookieStore()
const { data: eventsData, execute: fetchEventsByTags } = await useLazyAsyncData(
  `event-cards-events-data-${cookieStore.countryCookie}-${cookieStore.languageCookie}`,
  async () => {
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
        content_type: 'event',
        include: 10,
        locale: locale?.value,
        limit: 3,
        'fields.tags.sys.id[in]': [computedTags.value],
      })
    return response?.items || []
  },
  {
    transform(input) {
      return {
        events: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    immediate: false,
    watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
  },
)

const computedEvents = computed(() => {
  return eventsData.value?.events?.length
    ? eventsData.value.events
    : props.data?.fields?.references?.filter(instanceOfEvent)
})

onMounted(() => {
  // Prioritize articles by tag
  if (computedTags.value?.length) fetchEventsByTags()
})
</script>

<style lang="scss" module>
.config {
  --wrap-padding--base: 45px 20px 55px;
  --wrap-padding--md: 45px 20px 55px;
  --wrap-padding: var(--wrap-padding--base);

  --container-max-width: 1150px;
  --container-grid-template-columns--base: 1fr;
  --container-grid-template-columns--sm: 4fr 6fr;
  --container-gap--base: 45px;
  --container-gap--lg: 90px;

  --header-flex-direction--base: column;
  --header-flex-direction--sm: column;
  --header-flex-direction: var(--header-flex-direction--base);

  --cards-gap--base: 17px;
  --cards-gap--sm: 14px;
  --cards-gap: var(--cards-gap--base);

  --cards-grid-template-columns--base: 1fr;
  --cards-grid-template-columns--sm: 1fr;
  --cards-grid-template-columns: var(--cards-grid-template-columns--base);

  @include media('sm') {
    --header-flex-direction: var(--header-flex-direction--sm);
    --cards-gap: var(--cards-gap--sm);
    --cards-grid-template-columns: var(--cards-grid-template-columns--sm);
  }

  @include media('md') {
    --wrap-padding: var(--wrap-padding--md);
  }
}

.wrap {
  padding: var(--wrap-padding);
  background: var(--gray-2);
  border: solid #c0c0c0;
  border-width: 1px 0;
}

.container {
  align-items: center;
  max-width: var(--container-max-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: var(--container-grid-template-columns--base);
  gap: var(--container-gap--base);

  @include media('sm') {
    grid-template-columns: var(--container-grid-template-columns--sm);
  }

  @include media('lg') {
    gap: var(--container-gap--lg);
  }
}

.header {
  @include media('sm') {
    margin-top: 60px;
  }
}

.heading {
  @include fluid-type(28px, 52px);
  font-family: var(--roboto-condensed);
}

.copy {
  margin-bottom: 20px;

  @include media('sm') {
    margin-bottom: 60px;
  }
}

.cards {
  display: grid;
  grid-template-columns: var(--cards-grid-template-columns);
  gap: var(--cards-gap);
}
</style>
